import {
  LoaderFunction,
  useParams,
  ActionFunction,
  useFetcher,
} from 'react-router-dom';
import { IFetchApi } from '../../utils/types';
import { apiBatch, api } from '../../utils/fetchApi';

import { useDocumentCenterData, useSignedDocumentUrl } from './utils/hooks';
import { DocumentPreviewPanel } from './components/DocumentPreviewPanel';
import { useRef, useState } from 'react';
import clsx from 'clsx';
import { CertificatesInfoPanel } from './components/CertificatesInfoPanel';
import { Loading } from '../Loading';
import { NoDocumentsFoundPanel } from './components/NoDocumentsFoundPanel';

const formatDate = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export const loader: LoaderFunction = async () => {
  const calls: Record<string, IFetchApi> = {
    categories: {
      url: '/api/documents/categories',
      method: 'GET',
    },
    types: {
      url: '/api/documents/types',
      method: 'GET',
    },
    documents: {
      url: '/api/documents',
      method: 'GET',
    },
    workflowShareDocuments: {
      url: '/api/documents/workflow_share_documents',
      method: 'GET',
    },
  };

  return apiBatch(calls);
};

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.formData();

  await api({
    url: `/api/documents/upload`,
    method: 'POST',
    body: formData,
    contentType: '',
  });

  return null;
};

export const Component = () => {
  const { documentId: documentId } = useParams();

  const fetcher = useFetcher();
  const uploading = fetcher.state === 'submitting';

  const { types } = useDocumentCenterData();

  const {
    url: downloadUrl,
    status: signedUrlStatus,
    matchedDocument,
  } = useSignedDocumentUrl();

  const type = types.find((t) => t.id === matchedDocument?.document_type_id);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragEnter = () => setIsDragging(true);
  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type_public_id', documentId!);

    fetcher.submit(formData, {
      method: 'post',
      encType: 'multipart/form-data',
    });
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type_public_id', documentId!);

    fetcher.submit(formData, {
      method: 'post',
      encType: 'multipart/form-data',
    });
  };

  const onDownloadClick = async () => {
    if (!downloadUrl) return;
    const mailTitle = `${type?.name}.pdf`;
    const response = await fetch(downloadUrl, {
      method: 'GET',
    });
    const data = await response.blob();
    const objectURL = URL.createObjectURL(data);
    const alink = document.createElement('a');

    alink.href = objectURL;
    alink.download = mailTitle;
    alink.click();
  };

  if (matchedDocument == null || !type) {
    return (
      <div className="flex size-full h-full items-center justify-center rounded ">
        <NoDocumentsFoundPanel
          title={'Document not found'}
          description={'We could not find the document you are looking for'}
        />
      </div>
    );
  }

  if (uploading) {
    return <CertificatesInfoPanel text="Uploading..." />;
  }

  if (signedUrlStatus === 'loading') {
    return <Loading />;
  }

  if (signedUrlStatus === 'ready' && downloadUrl) {
    return (
      <DocumentPreviewPanel
        typeName={type.name}
        issueDate={
          matchedDocument?.issue_date
            ? formatDate(matchedDocument.issue_date)
            : ''
        }
        downloadUrl={downloadUrl}
        onDownloadClick={onDownloadClick}
      />
    );
  }

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{type?.name ?? 'Document'}</h2>
      </div>

      <div
        className="h-[41rem] rounded border-4 border-dashed bg-white p-2"
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className={clsx(
            'flex flex-col items-center justify-center',
            'size-full rounded  bg-sage-400 p-10 text-center',
            isDragging ? 'border-gray-400 bg-gray-300' : 'border-gray-300',
          )}
        >
          <p className="mb-1 text-center text-4xl font-bold text-zinc-700">
            {isDragging ? 'Release to upload' : 'Drag PDF here'}
          </p>

          <p className="text-center text-[16px] font-medium leading-[24px] text-zinc-700">
            or{' '}
            <span
              className="cursor-pointer text-teal-800 underline"
              onClick={handleBrowseClick}
            >
              browse
            </span>{' '}
            your computer.
          </p>

          <fetcher.Form
            method="post"
            encType="multipart/form-data"
            className="hidden"
            ref={formRef}
          >
            <input
              ref={fileInputRef}
              name="file"
              type="file"
              onChange={handleFileChange}
            />
            <input
              type="hidden"
              name="document_type_public_id"
              value={documentId}
            />
          </fetcher.Form>
        </div>
      </div>
    </>
  );
};

export { IndexRedirect } from './IndexRedirect';
